import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import type { AnnouncementWithAudio } from "@/lib/announcement-audio";
import { announcementsApi } from "@/lib/api/client";
import { useNetworkStatus } from "@/context/network-context";

/** Active site announcements, filtered to the member's live connectivity
 *  (on-site vs remote). Polled every 30s as a safety net; the realtime WS
 *  push (see ``use-announcement-socket`` / ``portal-socket``) refetches on
 *  change. Shared query key so the host (banner/corner/fullscreen) and the
 *  header indicator dedupe onto one request AND see the same filtered list. */
export function useAnnouncements(): AnnouncementWithAudio[] {
  const { status } = useNetworkStatus();
  const { data } = useQuery({
    queryKey: ["announcements"],
    queryFn: () => announcementsApi.list().then((r) => r.data),
    refetchInterval: 30_000,
    staleTime: 0,
  });

  return useMemo(() => {
    const all = data?.announcements ?? [];
    return all.filter((a) => matchesConnectivity(a, status));
  }, [data, status]);
}

/** Show an announcement based on its ``connectivity`` audience vs the
 *  member's network status. ``wrong_network`` (in a building, wrong VLAN)
 *  counts as on-site — they're physically present. While ``detecting`` we
 *  only show ``all`` so a targeted row never flashes to the wrong audience. */
function matchesConnectivity(a: AnnouncementWithAudio, status: string): boolean {
  const connectivity = a.connectivity ?? "all";
  if (connectivity === "all") return true;
  if (status === "detecting") return false;
  const onSite = status === "own_network" || status === "wrong_network";
  return connectivity === "on_site" ? onSite : !onSite;
}
